import {
  AlertDialog as ChakraUIAlertDialog,
  AlertDialogProps as ChakraUIAlertDialogProps,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Box
} from "@chakra-ui/react";

import { MouseEventHandler, ReactNode, useRef } from "react";

import { Button } from "../button/button";

export interface AlertDialogProps
  extends Pick<ChakraUIAlertDialogProps, "isOpen" | "onClose"> {
  header: string;
  body: string;
  buttonPrimary?: string;
  buttonSecondary?: string;
  buttonPrimaryVariant?: string;
  isPrimaryDestructive?: boolean;
  buttonSecondaryVariant?: string;
  isSecondaryDestructive?: boolean;
  onPrimaryClickHandler?: MouseEventHandler<HTMLButtonElement>;
  onSecondaryClickHandler?: MouseEventHandler<HTMLButtonElement>;
}

interface ButtonHandlerProps {
  children?: ReactNode;
  variant?: string;
  isDestructive?: boolean;
  onClickHandler?: MouseEventHandler<HTMLButtonElement>;
}

function AlertDialog(props: AlertDialogProps) {
  const {
    isOpen,
    onClose,
    header,
    body,
    buttonPrimary,
    buttonSecondary,
    buttonPrimaryVariant,
    isPrimaryDestructive,
    buttonSecondaryVariant,
    isSecondaryDestructive,
    onPrimaryClickHandler,
    onSecondaryClickHandler
  } = props;
  const cancelRef = useRef<HTMLDivElement | null>(null);

  const ButtonHandler = ({
    children,
    variant,
    isDestructive,
    onClickHandler
  }: ButtonHandlerProps): JSX.Element => {
    return (
      <Button
        variant={variant}
        isDestructive={isDestructive}
        onClick={onClickHandler}
        size={{ base: "lg", xl: "md" }}
        px={6}
        py={3}
        minW="116px !important"
        maxW={40}
      >
        {children}
      </Button>
    );
  };

  return (
    <ChakraUIAlertDialog
      motionPreset="slideInBottom"
      leastDestructiveRef={cancelRef}
      onClose={onClose}
      isOpen={isOpen}
      isCentered
    >
      <AlertDialogOverlay />
      <AlertDialogContent
        m={4}
        py={6}
        px={4}
        gap={6}
        w={{ base: "full", lg: 448 }}
        minW={288}
        maxW={448}
        minH={60}
        boxShadow="lg"
      >
        <Box
          display="flex"
          flexDirection="column"
          minH={{ base: 120, lg: 128 }}
          justifyContent="center"
          alignItems="flex-start"
          gap={2}
          alignSelf="stretch"
        >
          <AlertDialogHeader
            p={0}
            fontSize="lg"
            alignItems="flex-start"
            alignSelf="stretch"
          >
            {header}
          </AlertDialogHeader>
          <AlertDialogBody p={0} fontSize="sm" display="contents">
            {body}
          </AlertDialogBody>
        </Box>
        <AlertDialogFooter
          p={0}
          alignItems="flex-start"
          gap={6}
          alignSelf="stretch"
        >
          <>
            {buttonSecondary && (
              <ButtonHandler
                variant={buttonSecondaryVariant}
                isDestructive={isSecondaryDestructive}
                onClickHandler={onSecondaryClickHandler}
              >
                {buttonSecondary}
              </ButtonHandler>
            )}
            {buttonPrimary && (
              <ButtonHandler
                variant={buttonPrimaryVariant}
                isDestructive={isPrimaryDestructive}
                onClickHandler={onPrimaryClickHandler}
              >
                {buttonPrimary}
              </ButtonHandler>
            )}
          </>
        </AlertDialogFooter>
      </AlertDialogContent>
    </ChakraUIAlertDialog>
  );
}

export { AlertDialog };
